export function createEmptyScheduleState() {
  return {
    enabled: false,
    mode: "once",
    date: "",
    time: "",
    weekdays: [],
    times: ["08:00"],
  };
}

export function createStateFromSchedule(schedule) {
  const base = createEmptyScheduleState();
  if (!schedule) {
    return { ...base, enabled: true };
  }

  const config = (schedule.config || {});
  if (schedule.type === "RECURRING") {
    const rawWeekdays = Array.isArray(config.weekdays) ? config.weekdays : [];
    const weekdays = rawWeekdays
      .map((day) => Number(day))
      .filter((day) => !Number.isNaN(day) && day >= 0 && day <= 6);
    const rawTimes = Array.isArray(config.times) ? config.times : [];
    const times = rawTimes.length ? rawTimes.map((time) => `${time}`) : ["08:00"];
    return {
      ...base,
      enabled: true,
      mode: "recurring",
      weekdays,
      times,
    };
  }

  const runAtLocal = config.runAtLocal || "";
  let datePart = "";
  let timePart = "";
  if (typeof runAtLocal === "string" && runAtLocal.includes("T")) {
    const [date, time] = runAtLocal.split("T");
    datePart = date || "";
    timePart = time ? time.slice(0, 5) : "";
  } else if (schedule.nextRunAt) {
    const next = new Date(schedule.nextRunAt);
    if (!Number.isNaN(next.getTime())) {
      const year = next.getFullYear();
      const month = String(next.getMonth() + 1).padStart(2, "0");
      const day = String(next.getDate()).padStart(2, "0");
      const hours = String(next.getHours()).padStart(2, "0");
      const minutes = String(next.getMinutes()).padStart(2, "0");
      datePart = `${year}-${month}-${day}`;
      timePart = `${hours}:${minutes}`;
    }
  }

  return {
    ...base,
    enabled: true,
    mode: "once",
    date: datePart,
    time: timePart,
  };
}

function normalizeTime(value) {
  if (!value) return null;
  const match = `${value}`.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function buildScheduleRequest(state) {
  if (!state?.enabled) {
    return { valid: false, reason: "disabled", payload: null };
  }

  const timezoneOffsetMinutes = new Date().getTimezoneOffset();

  if (state.mode === "recurring") {
    const uniqueWeekdays = Array.from(
      new Set((state.weekdays || []).map((day) => Number(day)).filter((day) => day >= 0 && day <= 6))
    );
    const times = (state.times || [])
      .map((time) => normalizeTime(time))
      .filter((time) => Boolean(time));
    const uniqueTimes = Array.from(new Set(times));

    if (!uniqueWeekdays.length) {
      return {
        valid: false,
        reason: "weekdays",
        message: "Selecione pelo menos um dia da semana.",
        payload: null,
      };
    }

    if (!uniqueTimes.length) {
      return {
        valid: false,
        reason: "times",
        message: "Informe pelo menos um horário.",
        payload: null,
      };
    }

    return {
      valid: true,
      reason: "recurring",
      payload: {
        mode: "recurring",
        weekdays: uniqueWeekdays,
        times: uniqueTimes,
        timezoneOffsetMinutes,
      },
    };
  }

  const date = state.date?.trim();
  const time = normalizeTime(state.time);

  if (!date || !time) {
    return {
      valid: false,
      reason: "once",
      message: "Informe a data e horário do agendamento.",
      payload: null,
    };
  }

  const runAtLocal = `${date}T${time}`;
  const parts = runAtLocal.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!parts) {
    return {
      valid: false,
      reason: "once",
      message: "Data ou horário inválidos.",
      payload: null,
    };
  }

  const [_, year, month, day, hour, minute] = parts.map(Number);
  const runAtUtc = Date.UTC(year, month - 1, day, hour, minute);
  const runAt = new Date(runAtUtc + timezoneOffsetMinutes * 60000);

  if (Number.isNaN(runAt.getTime()) || runAt.getTime() <= Date.now()) {
    return {
      valid: false,
      reason: "once",
      message: "Escolha um horário futuro para o agendamento.",
      payload: null,
    };
  }

  return {
    valid: true,
    reason: "once",
    payload: {
      mode: "once",
      runAtLocal,
      timezoneOffsetMinutes,
    },
  };
}

